import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { Checkbox } from '../../../components/ui/Checkbox';


const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        setError(error?.message || 'Failed to login. Please try again.');
        return;
      }

      if (data?.user) {
        // Fetch user profile to get role
        const { data: profile } = await import('../../../services/authService')?.then(
          module => module.authService?.getUserProfile(data?.user?.id)
        );
        
        setSuccess('Login successful! Redirecting...');
        
        // Role-based redirect
        setTimeout(() => {
          if (profile?.role === 'employer') {
            navigate('/employer-dashboard');
          } else {
            navigate('/job-seeker-dashboard');
          }
        }, 1000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
        <p className="text-sm font-medium text-foreground">Demo Credentials:</p>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Job Seeker:</span> priya.sharma@email.com / JobSeeker@123
          </p>
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Employer:</span> recruiter@techcorp.in / Employer@456
          </p>
        </div>
      </div>

      <Input
        type="email"
        name="email"
        label="Email Address"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        disabled={isLoading}
      />
      <Input
        type="password"
        name="password"
        label="Password"
        placeholder="Enter your password"
        value={formData?.password}
        onChange={handleInputChange}
        error={errors?.password}
        required
        disabled={isLoading}
      />
      <div className="flex items-center justify-between">
        <Checkbox
          name="rememberMe"
          label="Remember me"
          checked={formData?.rememberMe}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-smooth"
          disabled={isLoading}
        >
          Forgot Password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
      >
        Sign In
      </Button>
    </form>
  );
};

export default LoginForm;